import {
  BreadcrumbItem,
  HelpArticle,
  HelpCollection,
  HelpIndex,
  helpIndexCache,
} from "@/types/help.types.ts";
import { DEFAULT_HELP_LANGUAGE } from "@/utils/help-language.ts";
import matter from "gray-matter";
import { useCallback, useEffect, useState } from "react";
// import { useLanguage } from "./useLanguage";

export const useHelp = (lang: string) => {
  const [helpIndex, setHelpIndex] = useState<HelpIndex | null>(null);
  const [syncedLang, setSyncedLang] = useState(lang);

  if (lang !== syncedLang) {
    setSyncedLang(lang);
    setHelpIndex(null);
  }

  // const [contentCache, setContentCache] = useState<Map<string, string>>(new Map());
  // const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadHelpIndex = async () => {
      try {
        const index = await helpIndexCache.getIndex(lang);
        setHelpIndex(index);
      } catch (err) {
        console.log("Failed to load help index:", err);
      }
    };

    loadHelpIndex();
  }, [lang]);

  const loadContent = useCallback(
    async (filePath: string, contentLanguage = lang): Promise<string> => {
      try {
        const timestamp = Date.now();
        const response = await fetch(`/help/${contentLanguage}/${filePath}?t=${timestamp}`);

        if (!response.ok) {
          throw new Error(`Failed to load help content: ${response.status}`);
        }

        const fullContent = await response.text();
        const { content } = matter(fullContent);
        return content;
      } catch (error) {
        if (contentLanguage !== DEFAULT_HELP_LANGUAGE) {
          try {
            const fallbackTimestamp = Date.now();
            const fallbackResponse = await fetch(
              `/help/${DEFAULT_HELP_LANGUAGE}/${filePath}?t=${fallbackTimestamp}`
            );

            if (fallbackResponse.ok) {
              const fallbackContent = await fallbackResponse.text();
              const { content } = matter(fallbackContent);
              return content;
            }
          } catch (fallbackError) {
            console.error(`Failed to load fallback content ${filePath}:`, fallbackError);
          }
        }

        console.error(`Failed to load content ${filePath}:`, error);
        return "";
      }
    },
    [lang]
  );

  const findCategoryBySlug = useCallback(
    (slug: string): HelpCollection | null => {
      if (!helpIndex) return null;

      const findInCategories = (categories: HelpCollection[]): HelpCollection | null => {
        for (const cat of categories) {
          if (cat.id === slug) return cat;
          if (cat.children) {
            const found = findInCategories(cat.children);
            if (found) return found;
          }
        }
        return null;
      };

      return findInCategories(helpIndex.categories);
    },
    [helpIndex]
  );

  const findArticleBySlug = useCallback(
    (slug: string): HelpArticle | null => {
      if (!helpIndex) return null;
      return helpIndex.articles.find((art) => art.id === slug) || null;
    },
    [helpIndex]
  );

  const findParentCategory = useCallback(
    (targetCategory: HelpCollection): HelpCollection | null => {
      if (!helpIndex) return null;

      const findParent = (categories: HelpCollection[]): HelpCollection | null => {
        for (const cat of categories) {
          if (cat.children?.some((child) => child.id === targetCategory.id)) {
            return cat;
          }
          if (cat.children) {
            const found = findParent(cat.children);
            if (found) return found;
          }
        }
        return null;
      };

      return findParent(helpIndex.categories);
    },
    [helpIndex]
  );
  const getCategoryPath = useCallback(
    (category: HelpCollection): BreadcrumbItem[] => {
      if (!helpIndex) return [];

      const path: BreadcrumbItem[] = [];
      const visited = new Set<string>();

      const buildPath = (cat: HelpCollection) => {
        if (visited.has(cat.id)) return;
        visited.add(cat.id);

        const parent = findParentCategory(cat);
        if (parent) {
          buildPath(parent);
        }

        path.push({
          id: cat.id,
          title: cat.title,
          url: cat.id ? `/help/collection/${cat.id}` : "/help",
        });
      };

      buildPath(category);
      return path;
    },
    [findParentCategory, helpIndex]
  );

  return {
    helpIndex,
    // loading,
    loadContent,
    findCategoryBySlug,
    getCategoryPath,
    findArticleBySlug,
    // getArticlesByCategory,
  };
};
